import { useEffect, useRef, useState } from 'preact/hooks';
import { createIsland } from '@/utils/islands';
import { criteriaToParams, type SearchCriteria } from '@sitecorecomponents/RealEstate/ResidencesByPropertySearch/searchCriteria';
import { hasValidToken } from '../Store/tokenstore';
import { openLogin } from '../Login/login';
import { deleteSearchAgent, getSearchAgents, setSearchAgentActive, type SearchAgent } from './searchAgentApi';
import { agentFilterNames, agentToCriteria, summarizeCriteria } from './searchAgentMapping';
import { label } from '@/utils/label';
import { SEARCH_AGENT_PARAM, SEARCH_PAGE_URL } from './searchAgentTexts';

/** Datasource fields, see SearchAgentsModel. */
type SearchAgentsProps = {
    loadingText?: string | null;
    errorText?: string | null;
    introHtml?: string | null;
    emptyHtml?: string | null;
    loginText?: string | null;
    loginButtonText?: string | null;
    goToSearchText?: string | null;
    showResultsText?: string | null;
    editText?: string | null;
    deleteText?: string | null;
    deleteConfirmText?: string | null;
    cancelText?: string | null;
    pauseText?: string | null;
    resumeText?: string | null;
    pausedText?: string | null;
    untitledText?: string | null;
    noFiltersText?: string | null;
};

function searchUrl(page: string, criteria: SearchCriteria, extra: Record<string, string> = {}) {
    const attributes = [...Object.keys(criteria.values), ...Object.keys(criteria.ranges)];
    const params = criteriaToParams(criteria, new URLSearchParams(extra), attributes).toString();
    return params ? `${page}?${params}` : page;
}

export default function SearchAgentsIsland(props: SearchAgentsProps) {
    const [loggedIn, setLoggedIn] = useState(hasValidToken());
    const [agents, setAgents] = useState<SearchAgent[] | null>(null);
    const [error, setError] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [confirming, setConfirming] = useState<SearchAgent | null>(null);
    const dialogRef = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (confirming && dialog && !dialog.open) dialog.showModal();
    }, [confirming]);

    useEffect(() => {
        if (!loggedIn) return;
        getSearchAgents()
            .then(setAgents)
            .catch(err => {
                console.error('Error loading search agents', err);
                setError(true);
            });
    }, [loggedIn]);

    const replace = (agent: SearchAgent) => setAgents(current => current?.map(a => (a.id === agent.id ? agent : a)) ?? null);

    const run = async (id: string, action: () => Promise<void>) => {
        setBusyId(id);
        try {
            await action();
        } catch (err) {
            console.error('Error updating search agent', err);
            setError(true);
        } finally {
            setBusyId(null);
        }
    };

    const toggleActive = (agent: SearchAgent) =>
        run(agent.id, async () => replace(await setSearchAgentActive(agent.id, !agent.isActive)));

    const remove = (agent: SearchAgent) => {
        setConfirming(null);
        void run(agent.id, async () => {
            await deleteSearchAgent(agent.id);
            setAgents(current => current?.filter(a => a.id !== agent.id) ?? null);
        });
    };

    if (!loggedIn) {
        return (
            <div class="bg-light-sand p-8">
                <p class="mb-4">{label(props.loginText, 'Log ind for at se dine boligagenter.')}</p>
                <button type="button" class="button" onClick={async () => setLoggedIn(await openLogin())}>
                    {label(props.loginButtonText, 'Log ind')}
                </button>
            </div>
        );
    }

    if (error) {
        return (
            <div class="bg-pka-red/5 p-4 border border-pka-red/20 text-pka-red">
                {label(props.errorText, 'Dine boligagenter kunne ikke hentes. Prøv igen senere.')}
            </div>
        );
    }

    if (!agents) {
        return (
            <div class="flex justify-center items-center py-8">
                <span class="border-pka-red border-b-2 rounded-full w-8 h-8 animate-spin" />
                <span class="ml-3">{label(props.loadingText, 'Henter dine boligagenter...')}</span>
            </div>
        );
    }

    if (agents.length === 0) {
        return (
            <div class="bg-light-sand p-8">
                {props.emptyHtml
                    ? <div class="mb-4 rich-text" dangerouslySetInnerHTML={{ __html: props.emptyHtml }} />
                    : <p class="mb-4">Du har ingen boligagenter endnu. Find en bolig, og gem din søgning som boligagent.</p>}
                <a class="button" href={SEARCH_PAGE_URL}>{label(props.goToSearchText, 'Find bolig')}</a>
            </div>
        );
    }

    return (
        <>
            {confirming && (
                <dialog
                    ref={dialogRef}
                    onClose={() => setConfirming(null)}
                    onClick={e => { if (e.target === dialogRef.current) dialogRef.current?.close(); }}
                >
                    <div class="modal-body">
                        <button
                            type="button"
                            class="top-3 right-3 absolute place-items-center grid bg-almost-black rounded-full w-9 h-9 cursor-pointer modal-close"
                            aria-label="Luk"
                            onClick={() => dialogRef.current?.close()}
                        >
                            <span class="block bg-white w-5 h-5" style="mask: var(--icon-functionClose) center / contain no-repeat;" />
                        </button>
    
                        <h3 class="mb-4">{label(props.deleteConfirmText, 'Vil du slette boligagenten?')}</h3>
                        <p class="mb-6 font-semibold text-almost-black">{confirming.name || label(props.untitledText, 'Boligagent')}</p>
    
                        <div class="flex flex-col gap-3">
                            <button type="button" class="w-full button" onClick={() => remove(confirming)}>
                                {label(props.deleteText, 'Slet')}
                            </button>
                            <button type="button" class="text-pka-red hover:text-bordeaux-red underline cursor-pointer" onClick={() => dialogRef.current?.close()}>
                                {label(props.cancelText, 'Annuller')}
                            </button>
                        </div>
                    </div>
                </dialog>
            )}
            {props.introHtml && <div class="mb-8 rich-text" dangerouslySetInnerHTML={{ __html: props.introHtml }} />}
            <ul class="flex flex-col gap-4">
                {agents.map(agent => {
                    const criteria = agentToCriteria(agent);
                    const chips = summarizeCriteria(criteria, agentFilterNames(agent));
                    return (
                        <li key={agent.id} class={`flex md:flex-row flex-col md:items-center gap-4 bg-light-sand px-4 py-3 border border-medium-sand rounded-md ${agent.isActive ? '' : 'opacity-70'}`}>
                            <div class="flex-1 min-w-0">
                                <p class="font-semibold text-almost-black">
                                    {agent.name || label(props.untitledText, 'Boligagent')}
                                    {!agent.isActive && (
                                        <span class="ml-2 font-normal text-dark-sand text-sm">({label(props.pausedText, 'på pause')})</span>
                                    )}
                                </p>
                                <ul class="flex flex-wrap gap-2 mt-2">
                                    {(chips.length > 0 ? chips : [label(props.noFiltersText, 'Alle boliger')]).map(chip => (
                                        <li key={chip} class="bg-white px-3 py-1 border border-medium-sand rounded-md text-sm">{chip}</li>
                                    ))}
                                </ul>
                            </div>
    
                            <div class="flex items-center gap-4 shrink-0">
                                <a class="button-functional-small" href={searchUrl(SEARCH_PAGE_URL, criteria)}>
                                    {label(props.showResultsText, 'Se resultater')}
                                </a>
                                <button
                                    type="button"
                                    class="text-pka-red hover:text-bordeaux-red text-sm underline cursor-pointer"
                                    disabled={busyId === agent.id}
                                    onClick={() => toggleActive(agent)}
                                >
                                    {agent.isActive ? label(props.pauseText, 'Sæt på pause') : label(props.resumeText, 'Genoptag')}
                                </button>
                                <a
                                    class="group w-6 h-6 shrink-0"
                                    href={searchUrl(SEARCH_PAGE_URL, criteria, { [SEARCH_AGENT_PARAM]: agent.id })}
                                    aria-label={label(props.editText, 'Redigér')}
                                    title={label(props.editText, 'Redigér')}
                                >
                                    <span class="block bg-pka-red group-hover:bg-bordeaux-red w-full h-full" style="mask: var(--icon-pencil) center/contain no-repeat;" />
                                </a>
                                <button
                                    type="button"
                                    class="group w-6 h-6 cursor-pointer shrink-0"
                                    disabled={busyId === agent.id}
                                    aria-label={label(props.deleteText, 'Slet')}
                                    title={label(props.deleteText, 'Slet')}
                                    onClick={() => setConfirming(agent)}
                                >
                                    <span class="block bg-pka-red group-hover:bg-bordeaux-red w-full h-full" style="mask: var(--icon-wasteChute) center/contain no-repeat;" />
                                </button>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </>
    );
}

export function initSearchAgentsIsland() {
    createIsland('search-agents', SearchAgentsIsland);
}
